import React, { useState } from 'react';
import authService from '../../services/authService';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({
  isOpen,
  onClose,
  onSuccess,
  isForced = false,
  forcedReason = '',
  onLogout
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen) return null;

  const calculateStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score += 25;
    if (pwd.length >= 10) score += 25;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 25;
    if (/[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score += 25;
    return Math.min(100, score);
  };

  const strength = calculateStrength(newPassword);

  const getStrengthLabel = (score) => {
    if (score <= 25) return { label: 'Weak', color: '#ef4444' };
    if (score <= 50) return { label: 'Fair', color: '#f59e0b' };
    if (score <= 75) return { label: 'Good', color: '#3b82f6' };
    return { label: 'Strong', color: '#10b981' };
  };

  const strengthMeta = getStrengthLabel(strength);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!currentPassword) {
      setErrorMessage('Please enter your current password.');
      return;
    }
    if (!newPassword) {
      setErrorMessage('Please enter a new password.');
      return;
    }
    if (newPassword.length < 6) {
      setErrorMessage('New password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New password and confirm password do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setErrorMessage('New password cannot be the same as your current password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await authService.changePassword(currentPassword, newPassword, confirmNewPassword);
      if (res && res.success) {
        setSuccessMessage('Your password has been changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setTimeout(() => {
          if (onSuccess) onSuccess(res.data);
          if (onClose) onClose();
        }, 1200);
      } else {
        setErrorMessage(res.message || 'Failed to change password.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        err.message ||
        'Failed to change password. Please check your current password.';
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pwd-modal-overlay" role="dialog" aria-modal="true">
      <div className="pwd-modal-container">
        {/* Header */}
        <div className="pwd-modal-header">
          <div className="pwd-modal-header-icon">
            <span>🔐</span>
          </div>
          <div className="pwd-modal-header-text">
            <h3>{isForced ? 'Password Change Required' : 'Change Password'}</h3>
            <p>
              {isForced
                ? forcedReason || 'Your password has expired or an update is required by security policy.'
                : 'Update your account credentials to maintain high security.'}
            </p>
          </div>
          {!isForced && (
            <button
              type="button"
              className="pwd-modal-close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              ✕
            </button>
          )}
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="pwd-modal-form">
          {errorMessage && (
            <div className="pwd-alert pwd-alert-danger" role="alert">
              <span className="pwd-alert-icon">⚠️</span>
              <span className="pwd-alert-text">{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="pwd-alert pwd-alert-success" role="alert">
              <span className="pwd-alert-icon">✅</span>
              <span className="pwd-alert-text">{successMessage}</span>
            </div>
          )}

          {/* Current Password */}
          <div className="pwd-form-group">
            <label htmlFor="current-password">Current Password <span className="req">*</span></label>
            <div className="pwd-input-wrap">
              <input
                id="current-password"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                autoComplete="current-password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="pwd-eye-btn"
                onClick={() => setShowCurrent((p) => !p)}
                tabIndex={-1}
                aria-label="Toggle current password visibility"
              >
                {showCurrent ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="pwd-form-group">
            <label htmlFor="new-password">New Password <span className="req">*</span></label>
            <div className="pwd-input-wrap">
              <input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min. 6 characters)"
                autoComplete="new-password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="pwd-eye-btn"
                onClick={() => setShowNew((p) => !p)}
                tabIndex={-1}
                aria-label="Toggle new password visibility"
              >
                {showNew ? '👁️' : '🙈'}
              </button>
            </div>
            {newPassword && (
              <div className="pwd-strength-bar-wrap">
                <div className="pwd-strength-bar">
                  <div
                    className="pwd-strength-fill"
                    style={{ width: `${strength}%`, backgroundColor: strengthMeta.color }}
                  />
                </div>
                <span className="pwd-strength-label" style={{ color: strengthMeta.color }}>
                  Strength: {strengthMeta.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div className="pwd-form-group">
            <label htmlFor="confirm-new-password">Confirm New Password <span className="req">*</span></label>
            <div className="pwd-input-wrap">
              <input
                id="confirm-new-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Re-type new password"
                autoComplete="new-password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="pwd-eye-btn"
                onClick={() => setShowConfirm((p) => !p)}
                tabIndex={-1}
                aria-label="Toggle confirm password visibility"
              >
                {showConfirm ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {/* Security policy reminder */}
          <div className="pwd-policy-hint">
            <span className="pwd-policy-icon">ℹ️</span>
            <span>Note: You cannot reuse any of your last 5 previously used passwords.</span>
          </div>

          {/* Modal Actions */}
          <div className="pwd-modal-actions">
            {isForced ? (
              <button
                type="button"
                className="pwd-btn pwd-btn-secondary"
                onClick={onLogout}
                disabled={isSubmitting}
              >
                Sign Out Instead
              </button>
            ) : (
              <button
                type="button"
                className="pwd-btn pwd-btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="pwd-btn pwd-btn-primary"
              disabled={isSubmitting || !currentPassword || !newPassword || !confirmNewPassword}
            >
              {isSubmitting ? 'Updating Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
